// @flow

import { Component } from '../jsti/core';
import EventNames from '../constants/EventNames';
import Notifications from '../constants/Notifications';
import { focus } from '../tests/TestHelpers';
import AsyncRequest from '../classes/AsyncRequest';
import { url } from '../classes/Helpers';
import EventKeys from '../constants/EventKeys';
import type { SearchSuggestion } from '../interfaces/SearchSuggestion';

export default class SearchInput extends Component {
  #searchHandler: function;
  #previousValue: string = '';
  delayInMilliseconds: number = 200;
  originalValue: string;
  activeSuggestion: ?SearchSuggestion = null;

  /**
   * Initialize the component.
   *
   * @param pEl
   */
  bind(pEl: HTMLElement): void {
    super.bind(pEl);

    this.addEventListener(EventNames.FOCUS, this.onFocus.bind(this));
    this.addEventListener(EventNames.INPUT, this.onInput.bind(this));
    this.addEventListener(EventNames.KEY_DOWN, this.onKeyDown.bind(this));
    this.addListener(Notifications.SEARCH_SUGGESTION_TOUCH, this.focusOut.bind(this));
    this.addListener(Notifications.SEARCH_SUGGESTION_CHOSEN, this.onSuggestionUpdate.bind(this));

    this.addListener(Notifications.FOCUS_ON_SEARCH, () => {
      this.el.focus();
      focus(this.el);
    });

    this.searchSuggestions();
  }

  /**
   * Executes when the user focuses the search input.
   */
  onFocus(): void {
    this.notify(Notifications.FOCUSED_ON_SEARCH);
  }

  /**
   * Executes when the user types something in the search input.
   */
  onInput(): void {
    this.originalValue = this.el.value;
    this.activeSuggestion = null;

    clearTimeout(this.#searchHandler);
    this.#searchHandler = setTimeout(this.searchSuggestions.bind(this), this.delayInMilliseconds);
  }

  /**
   * Executes when the user presses on any key
   */
  onKeyDown(): void {
    switch (event.key) {
      case EventKeys.ESCAPE: {
        this.el.blur();
        break;
      }
      case EventKeys.ENTER: {
        if (this.activeSuggestion) {
          event.preventDefault();
          window.location.href = this.activeSuggestion.url;
        }
        break;
      }
      case EventKeys.ARROW_DOWN: {
        this.notify(Notifications.NEXT_SEARCH_SUGGESTION);
        break;
      }
      case EventKeys.ARROW_UP: {
        event.preventDefault();
        this.notify(Notifications.PREVIOUS_SEARCH_SUGGESTION);
        break;
      }
    }
  }

  /**
   * Executes when a new suggestion is chosen
   */
  onSuggestionUpdate({ suggestion }: { suggestion: ?SearchSuggestion }): void {
    this.activeSuggestion = suggestion;

    if (!this.activeSuggestion) {
      this.el.value = this.originalValue;
      return;
    }

    this.el.value = this.activeSuggestion.name;
  }

  /**
   * Start searching for search suggestions.
   */
  searchSuggestions(): void {
    if (!(this.el instanceof HTMLInputElement)) return;

    if (this.#previousValue === this.el.value) return;

    this.#previousValue = this.el.value;

    if (!this.el.value) {
      this.notify(Notifications.UPDATE_SEARCH_SUGGESTIONS);
      return;
    }
    if (this.el.value.length > 2) {
      AsyncRequest.post('', { keyword: this.el.value }, url('/search/suggestions'))
        .then(({ data: { suggestions, dataLayer } }: Object) => {
          this.notify(Notifications.UPDATE_SEARCH_SUGGESTIONS, {
            suggestions,
            dataLayer
          });
        })
        .catch(() => {
          this.notify((Notifications.UPDATE_SEARCH_SUGGESTIONS));
        });
    }
  }

  /**
   * removes the focus from the search input
   */
  focusOut(): void {
    this.el.blur();
  }
}
